/** The catalogue grid's bulk actions (plan §3.9): include / exclude, set any policy key, and
 *  decouple / re-couple — over the ticked rows, or over every row matching the filters ("select
 *  all matching": include or exclude by kind / category / package). The endpoints are
 *  synchronous, all or nothing, and take explicit ids (at most 500), so an all-matching
 *  selection is resolved to ids first.
 *
 *  Except `tour.join` with a value (plan 2026-10-11 product joins §9), which the server applies
 *  product by product: no consent can be given in bulk, so a product whose IDs another tour holds
 *  is left as it was and listed in `refused`, and the others join (`joins`). The dialog then stays
 *  open on what happened to each.
 *
 *  A refusal of a product others join (`join_target_in_use`: excluding it, or a value leaving it
 *  no tour) names its joiners from the row's `joined_by`, with what to do first. */
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import * as catalogueApi from '@/api/catalogue';
import type { BulkSettingsResult, CatalogueRegistry, CatalogueRow, CatalogueScope } from '@/api/catalogue';
import { AppDialog } from '@/components/AppDialog';
import { useSnackbar } from '@/components/Snackbar';

import { bulkCouplingTargets, bulkErrorLines, entityLabel, explainTargetInUse, KEY } from './catalogueModel';
import { JoinOutcomeLines } from './JoinOutcome';
import { useCatalogueProductRef, useInvalidateCatalogue } from './queries';
import { SettingControl } from './SettingControl';

/** Most products one bulk call takes (the backend's `BULK_MAX`). */
export const BULK_MAX = 500;

export interface BulkTarget {
  /** What the operator is acting on, for the copy. */
  count: number;
  phrase: string;
  /** The rows, resolved (an all-matching selection is fetched; throws past BULK_MAX). */
  resolve: () => Promise<CatalogueRow[]>;
}

type Mode = 'include' | 'exclude' | 'setting';

export function BulkSettingDialog({
  tenantId,
  scope,
  registry,
  mode,
  target,
  rowsByVendor,
  onClose,
  onDone,
}: {
  tenantId: number;
  /** The catalogue the rows belong to (a product-reference value is picked from it). */
  scope?: CatalogueScope;
  /** The page's rows by vendor id: names the joiners of a refusal beside the selection's own. */
  rowsByVendor?: ReadonlyMap<string, CatalogueRow>;
  registry: CatalogueRegistry;
  mode: Mode;
  target: BulkTarget;
  onClose: () => void;
  onDone: () => void;
}) {
  const { show } = useSnackbar();
  const invalidate = useInvalidateCatalogue(tenantId);
  // Any product-scope POLICY key (facts are corrected per product, in the drawer).
  const settable = useMemo(
    () => registry.settings.filter((s) => s.kind === 'policy' && s.scope === 'product'),
    [registry],
  );
  const [key, setKey] = useState<string>(mode === 'setting' ? (settable[0]?.key ?? '') : KEY.include);
  const spec = registry.settings.find((s) => s.key === key);
  const [value, setValue] = useState<unknown>(mode === 'include' ? true : mode === 'exclude' ? false : (spec?.default ?? null));
  const [reset, setReset] = useState(false);
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<string[] | null>(null);
  // tour.join only: what happened to each product (the dialog stays open on it).
  const [joinResult, setJoinResult] = useState<{ result: BulkSettingsResult; names: Map<number, string> } | null>(
    null,
  );
  const joining = key === KEY.join && !reset;
  const joinTarget = joining && typeof value === 'string' && value ? value : null;
  const into = useCatalogueProductRef(scope ?? null, joinTarget);

  const mutation = useMutation({
    onMutate: () => setErrors(null),
    mutationFn: async () => {
      const rows = await target.resolve();
      if (!rows.length) throw new Error('No product matches the selection.');
      const names = new Map(rows.map((r) => [r.id, r.title || r.name]));
      const byId = new Map(rows.map((r) => [r.id, r]));
      const byVendor = new Map(rows.map((r) => [r.vendor_id, r]));
      try {
        const result = await catalogueApi.bulkSettings(tenantId, {
          product_ids: rows.map((r) => r.id),
          key,
          value: reset ? null : (value as never),
          ...(note.trim() ? { note: note.trim() } : {}),
        });
        return { result, names };
      } catch (e) {
        const refusals = catalogueApi.settingErrorsOf(e);
        if (refusals?.length) {
          // A product others join (join_target_in_use): named from its row's joined_by.
          const explained = explainTargetInUse(
            refusals,
            (id) => byId.get(id)?.joined_by,
            (v) => byVendor.get(v) ?? rowsByVendor?.get(v),
          );
          setErrors(bulkErrorLines(explained, (id) => names.get(id)));
          throw new Error('refused');
        }
        throw e;
      }
    },
    onSuccess: async ({ result, names }) => {
      const n = result.changed_ids.length;
      show(n ? `${n} product${n === 1 ? '' : 's'} changed.` : 'Nothing changed — the products already had that setting.', 'success');
      for (const w of result.warnings) show(w.detail, 'warning', { autoHideDuration: 15000 });
      await invalidate();
      if (result.refused.length || result.joins.length) setJoinResult({ result, names });
      else onDone();
    },
  });

  const title =
    mode === 'include'
      ? 'Include in catalogue syncs'
      : mode === 'exclude'
        ? 'Exclude from catalogue syncs'
        : 'Set a setting';

  if (joinResult) {
    return (
      <BulkJoinResult
        result={joinResult.result}
        names={joinResult.names}
        intoName={into.data ? into.data.title || into.data.name : null}
        onDone={onDone}
      />
    );
  }

  return (
    <AppDialog
      open
      onClose={onClose}
      title={title}
      size="sm"
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            // A computed default arrives as null: never let "no value picked" travel as a reset.
            disabled={mutation.isPending || !key || (!reset && (value === null || value === undefined || value === ''))}
            onClick={() => mutation.mutate()}
          >
            {mode === 'setting' ? 'Apply' : mode === 'include' ? 'Include' : 'Exclude'}
          </Button>
        </>
      }
    >
      <Typography variant="body2" sx={{ mb: 2 }}>
        {mode === 'include' &&
          `Select ${target.phrase} for catalogue syncs. Nothing is built until a sync runs.`}
        {mode === 'exclude' &&
          `Leave ${target.phrase} out of catalogue syncs. What earlier syncs built stays as it is.`}
        {mode === 'setting' && `Set one setting on ${target.phrase} (an ops override on each).`}
      </Typography>

      {mode === 'setting' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
          <TextField
            select
            size="small"
            label="Setting"
            value={key}
            onChange={(e) => {
              const next = registry.settings.find((s) => s.key === e.target.value);
              setKey(e.target.value);
              setValue(next?.default ?? null);
              setErrors(null);
            }}
          >
            {settable.map((s) => (
              <MenuItem key={s.key} value={s.key}>
                {s.label}
                {s.applies_rule ? ` (${s.applies_rule})` : ''}
              </MenuItem>
            ))}
          </TextField>
          {spec && (
            <>
              <Typography variant="caption" color="text.secondary">
                {spec.help}
              </Typography>
              <FormControlLabel
                control={<Checkbox size="small" checked={reset} onChange={(e) => setReset(e.target.checked)} />}
                label="Reset to the default instead (drop ops overrides)"
              />
              {!reset && (
                <SettingControl
                  spec={spec}
                  value={value}
                  onChange={setValue}
                  label="Value"
                  ariaLabel={`${spec.label}: value`}
                  products={scope ? { scope } : undefined}
                />
              )}
              {joining && (
                <Typography variant="caption" color="text.secondary">
                  Applied product by product: a product whose IDs another tour holds is left as it was and
                  listed, since only its own settings drawer can agree to move them. The others join now.
                </Typography>
              )}
            </>
          )}
        </Box>
      )}

      <TextField
        label="Note (optional)"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        fullWidth
        size="small"
        inputProps={{ maxLength: 500 }}
      />

      {errors && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Nothing was changed:
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {errors.slice(0, 20).map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </Box>
          {errors.length > 20 && `…and ${errors.length - 20} more.`}
        </Alert>
      )}
      {mutation.isError && !errors && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {(mutation.error as Error).message}
        </Alert>
      )}
    </AppDialog>
  );
}

/** What a bulk `tour.join` did: the products that joined (each outcome), and the ones left as they
 *  were with the reason (`refused`). */
function BulkJoinResult({
  result,
  names,
  intoName,
  onDone,
}: {
  result: BulkSettingsResult;
  names: Map<number, string>;
  intoName: string | null;
  onDone: () => void;
}) {
  const refused = bulkErrorLines(result.refused, (id) => names.get(id));
  const refusedIds = new Set(result.refused.map((e) => e.product_id));
  const joined = result.joins.length;
  return (
    <AppDialog
      open
      onClose={onDone}
      title="Set a setting"
      size="sm"
      actions={
        <Button variant="contained" onClick={onDone}>
          Done
        </Button>
      }
    >
      {joined > 0 && (
        <Alert severity="success" sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}>
          <AlertTitle>
            {joined} product{joined === 1 ? '' : 's'} joined
          </AlertTitle>
          {result.joins.slice(0, 20).map((o) => (
            <Box key={o.id} sx={{ mb: 1 }}>
              <JoinOutcomeLines outcome={o} name={names.get(o.id) ?? o.vendor_id} intoName={intoName ?? undefined} />
            </Box>
          ))}
          {joined > 20 && `…and ${joined - 20} more.`}
        </Alert>
      )}
      {refused.length > 0 && (
        <Alert severity="warning">
          <AlertTitle>
            {refusedIds.size} product{refusedIds.size === 1 ? '' : 's'} left as they were
          </AlertTitle>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {refused.slice(0, 20).map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </Box>
          {refused.length > 20 && `…and ${refused.length - 20} more.`}
          <Typography variant="body2" sx={{ mt: 1 }}>
            Open a product’s settings to see who holds its IDs and agree to move them there.
          </Typography>
        </Alert>
      )}
    </AppDialog>
  );
}

const COUPLING_ENTITIES = ['tour', 'itinerary', 'activity', 'tag'] as const;

export function BulkCouplingDialog({
  tenantId,
  coupled,
  target,
  onClose,
  onDone,
}: {
  tenantId: number;
  coupled: boolean;
  target: BulkTarget;
  onClose: () => void;
  onDone: () => void;
}) {
  const { show } = useSnackbar();
  const invalidate = useInvalidateCatalogue(tenantId);
  const [entities, setEntities] = useState<string[]>(['tour', 'itinerary']);
  const [note, setNote] = useState('');

  const mutation = useMutation({
    mutationFn: async () => {
      const rows = await target.resolve();
      const ids = bulkCouplingTargets(rows, entities, coupled);
      if (!ids.length) throw new Error(`No ${coupled ? 'decoupled' : 'coupled'} output of those kinds on the selected products.`);
      if (ids.length > BULK_MAX) throw new Error(`${ids.length} outputs — at most ${BULK_MAX} per call; narrow the selection.`);
      return catalogueApi.bulkCoupling(tenantId, {
        output_ids: ids,
        coupled,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
    },
    onSuccess: async (r) => {
      const n = r.changed_ids.length;
      show(`${n} output${n === 1 ? '' : 's'} ${coupled ? 're-coupled' : 'decoupled'}.`, 'success');
      await invalidate();
      onDone();
    },
  });

  return (
    <AppDialog
      open
      onClose={onClose}
      title={coupled ? 'Re-couple outputs' : 'Decouple outputs'}
      size="sm"
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            color={coupled ? 'primary' : 'warning'}
            disabled={mutation.isPending || entities.length === 0 || (!coupled && !note.trim())}
            onClick={() => mutation.mutate()}
          >
            {coupled ? 'Re-couple' : 'Decouple'}
          </Button>
        </>
      }
    >
      <Typography variant="body2" sx={{ mb: 1 }}>
        {coupled
          ? `Re-couple what syncs built from ${target.phrase}: the next explicit sync or applied change rebuilds them, overwriting edits made by hand.`
          : `Decouple what syncs built from ${target.phrase}: every later build leaves them out until they are re-coupled. Aliases follow their tour.`}
      </Typography>
      <FormGroup row sx={{ mb: 1 }}>
        {COUPLING_ENTITIES.map((e) => (
          <FormControlLabel
            key={e}
            control={
              <Checkbox
                size="small"
                checked={entities.includes(e)}
                onChange={(ev) =>
                  setEntities((prev) => (ev.target.checked ? [...prev, e] : prev.filter((x) => x !== e)))
                }
              />
            }
            label={`${entityLabel(e)}s`}
          />
        ))}
      </FormGroup>
      <TextField
        label={coupled ? 'Note (optional)' : 'Reason (required)'}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        fullWidth
        multiline
        minRows={2}
        size="small"
        inputProps={{ maxLength: 500 }}
      />
      {mutation.isError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {(mutation.error as Error).message}
        </Alert>
      )}
    </AppDialog>
  );
}
